/**
 * MOCK/TEMPORARY — Studio projects.
 *
 * Projects live in the client-side studioProjectsStore; this module is the seam that store
 * writes through, exactly as playlistsStore writes through playlistsApi. Nothing here
 * persists across a reload, which is the current, expected behaviour and is the same for
 * songs, playlists, credits and the player.
 *
 * A PROJECT IS THE USER'S OWN DATA — a name and a set of ids they chose. No generation, no
 * model, no audio. So unlike the Studio's AI tools there is nothing here that a backend
 * cannot straightforwardly implement: every function below is a one-line rewrite into
 * `fetch`, and the store above it does not change.
 *
 * WHAT IT DELIBERATELY DOES NOT DO, because Batch 6 §3 is explicit about both:
 *
 *   IT STORES NO MUSIC. `songIds` are references into the library the app already has.
 *   There is no second music store, no copy of a song and no upload path here — the
 *   Studio's audio upload is a separate boundary with its own missing capability, stated
 *   in api/audioUploadApi.ts, and it is untouched.
 *
 *   IT DOES NOT PRETEND TO PERSIST. `getProjects` returns the empty set rather than
 *   fabricating a project list, and the store keeps what the session creates in memory
 *   and says so on screen. A reload loses projects exactly as it loses playlists.
 *
 * Real endpoints: docs/api-contract.md, "Studio projects".
 */
import { USE_MOCK_API } from '../config';
import type { ProjectSongScope, StudioProject } from '../types/domain';
import { ApiError } from './errors';
import { http } from './http';
import { assertMock, mockDelay } from './mock';

/** Longest acceptable project name. Matches the account name's limit. */
export const MAX_PROJECT_NAME_LENGTH = 60;

/**
 * Why a project name is not acceptable, or null when it is.
 *
 * EXPORTED for the reason `authApi.nameError` is: the create form has to disable its
 * button and say why BEFORE a request is made, and the API has to refuse a bad name
 * regardless of what any form did. Two copies of that rule is how the two answers drift
 * apart, so there is one rule and both callers ask it.
 *
 * Returns the message rather than a boolean — "invalid" is not useful copy.
 */
export function projectNameError(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Name your project';
  if (trimmed.length > MAX_PROJECT_NAME_LENGTH) {
    return `Use ${MAX_PROJECT_NAME_LENGTH} characters or fewer`;
  }
  return null;
}

export const studioProjectsApi = {
  /**
   * MOCK: GET /studio/projects — returns the empty set.
   *
   * Deliberately empty rather than seeded, for the same reason playlistsApi.getPlaylists
   * is: the seed library exists because the app has to have songs to show, but inventing
   * projects the user never created would put fabricated work in a list whose entire
   * purpose is to hold what they set up — and the "create your first project" screen is a
   * designed state, not a fallback.
   */
  async getProjects(): Promise<StudioProject[]> {
    if (!USE_MOCK_API) {
      return http.get<StudioProject[]>('/studio/projects');
    }
    assertMock('studioProjectsApi.getProjects');
    return mockDelay([]);
  },

  /**
   * MOCK: POST /studio/projects
   *
   * VALIDATES, and this is the one function here that can refuse. The name is the only
   * field a user types, so it is the only one that can be wrong; `songIds` come from the
   * library they already own. A 422 is "understood and rejected" — see api/mock.ts — which
   * is what a bad name is, as opposed to the 501 a missing capability returns.
   */
  async createProject(
    name: string,
    songIds: string[],
    scope: ProjectSongScope
  ): Promise<void> {
    if (!USE_MOCK_API) {
      await http.post<StudioProject>('/studio/projects', { name, songIds, scope });
      return;
    }
    assertMock('studioProjectsApi.createProject');
    const problem = projectNameError(name);
    if (problem) throw new ApiError(422, problem);
    return mockDelay(undefined);
  },

  /** MOCK: PATCH /studio/projects/:id */
  async renameProject(id: string, name: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.patch<void>(`/studio/projects/${id}`, { name });
      return;
    }
    assertMock('studioProjectsApi.renameProject');
    const problem = projectNameError(name);
    if (problem) throw new ApiError(422, problem);
    return mockDelay(undefined);
  },

  /** MOCK: DELETE /studio/projects/:id */
  async deleteProject(id: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.delete<void>(`/studio/projects/${id}`);
      return;
    }
    assertMock('studioProjectsApi.deleteProject');
    return mockDelay(undefined);
  },

  /**
   * MOCK: PUT /studio/projects/:id/songs
   *
   * The whole set rather than an add/remove pair, following playlistsApi.reorderSongs's
   * reasoning: replacing the list is idempotent, so a client that retries after a dropped
   * response cannot apply the same change twice.
   */
  async setProjectSongs(id: string, songIds: string[]): Promise<void> {
    if (!USE_MOCK_API) {
      await http.request<void>(`/studio/projects/${id}/songs`, {
        method: 'PUT',
        body: { songIds },
      });
      return;
    }
    assertMock('studioProjectsApi.setProjectSongs');
    return mockDelay(undefined);
  },
};
